import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Image } from 'react-bootstrap';
import { User, Camera, Save, X, Upload } from 'lucide-react';
import api from '../services/api';
import { useAlert } from '../context/AlertContext';

interface UserProfileModalProps {
    show: boolean;
    onHide: () => void;
    onUpdate: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ show, onHide, onUpdate }) => {
    const [user, setUser] = useState({ first_name: '', last_name: '', email: '', photo: null as string | null });
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showAlert } = useAlert();

    useEffect(() => {
        if (show) {
            fetchUser();
        }
    }, [show]);

    const fetchUser = async () => {
        try {
            const response = await api.get('/company/me/');
            setUser(response.data);
            setPhotoPreview(null);
            setPhotoFile(null);
        } catch (error) {
            console.error('Error fetching profile:', error);
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('first_name', user.first_name);
            formData.append('last_name', user.last_name);
            formData.append('email', user.email);
            if (photoFile) {
                formData.append('photo', photoFile);
            }

            await api.patch('/company/me/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            showAlert('Perfil atualizado com sucesso!', { type: 'success' });
            onUpdate();
            onHide();
        } catch (error) {
            console.error('Error updating profile:', error);
            showAlert('Erro ao atualizar perfil.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const displayPhoto = photoPreview || user.photo;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold">Meu Perfil</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0">
                <Form onSubmit={handleSubmit}>
                    <div className="text-center my-4">
                        <div className="position-relative d-inline-block">
                            {displayPhoto ? (
                                <div
                                    className="position-relative d-inline-block"
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Image
                                        src={displayPhoto}
                                        roundedCircle
                                        style={{ width: '120px', height: '120px', objectFit: 'cover', border: '3px solid #f8f9fa' }}
                                        className="shadow-sm"
                                    />
                                </div>
                            ) : (
                                <div 
                                    className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto shadow-sm" 
                                    style={{ width: '120px', height: '120px', border: '3px solid #f8f9fa', cursor: 'pointer' }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <User size={48} className="text-secondary opacity-50" />
                                </div>
                            )}
                            <div 
                                className="position-absolute bottom-0 end-0 bg-white rounded-circle p-2 shadow-sm border cursor-pointer"
                                style={{ cursor: 'pointer', transform: 'translate(10%, 10%)' }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Camera size={18} className="text-primary" />
                            </div>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                        <Form.Text className="d-block mt-2 text-muted">
                            Clique na imagem para alterar
                        </Form.Text>
                    </div>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nome</Form.Label>
                                <Form.Control
                                    value={user.first_name}
                                    onChange={(e) => setUser({ ...user, first_name: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sobrenome</Form.Label>
                                <Form.Control
                                    value={user.last_name}
                                    onChange={(e) => setUser({ ...user, last_name: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-4">
                        <Form.Label>E-mail</Form.Label>
                        <Form.Control
                            value={user.email}
                            onChange={(e) => setUser({ ...user, email: e.target.value })}
                        />
                    </Form.Group>

                    <div className="d-grid">
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Salvando...' : 'Salvar Alterações'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default UserProfileModal;
